import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  loansApi,
  PAY_HEAD_OPTIONS,
  LOAN_TYPE_OPTIONS,
  EMPLOYEE_OPTIONS,
} from '../api/loansApi';
import { listAccountHeads } from '../../../masters/acheads/heads/api/acHeadApi';
import { appToast } from '../../../../components/AppToast';

export function useLoansEntry() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const idQuery = searchParams.get('id');

  const today = new Date().toISOString().split('T')[0];

  const [formData, setFormData] = useState({
    applyDate: today,
    payHead: 'None',
    pay_head_id: null,
    name: '',
    emp_id: null,
    employeeId: '',
    loanDate: today,
    loanType: 'Personal Loan',
    loan_type: 1,
    loanAmount: 0,
    guarantorName1: '',
    guarantor1_id: null,
    guarantorName2: '',
    guarantor2_id: null,
    emiFromDate: today,
    emiAmount: 0,
    reason: 'Personal Loan',
    remarks: '',
    status: 'Pending',
    company: 'All',
    branch: 'All',
    department: 'All',
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Dynamic dropdown options
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [accountHeadOptions, setAccountHeadOptions] = useState([]);
  const [payHeadOptions, setPayHeadOptions] = useState(PAY_HEAD_OPTIONS);
  const [loanTypeOptions, setLoanTypeOptions] = useState(LOAN_TYPE_OPTIONS);

  // Load dynamic options from Backend & AcHeads Master
  const loadDynamicOptions = useCallback(async () => {
    try {
      const [backendOpts, acHeadsResult] = await Promise.allSettled([
        loansApi.getOptions(),
        listAccountHeads(),
      ]);

      const bData = backendOpts.status === 'fulfilled' && backendOpts.value ? backendOpts.value : null;
      const acList = acHeadsResult.status === 'fulfilled' && Array.isArray(acHeadsResult.value) ? acHeadsResult.value : [];

      // 1. Account Heads (General Ledger Chart of Accounts for Guarantors)
      let combinedAcHeads = [];
      if (bData?.account_heads?.length > 0) {
        combinedAcHeads = bData.account_heads.map((h) => ({
          value: String(h.id || h.value),
          id: h.id || h.value,
          name: h.name,
          label: h.label || `${h.name} (${h.group || 'General'})`,
          group: h.group || 'General',
        }));
      }
      for (const h of acList) {
        if (!combinedAcHeads.some((ah) => String(ah.id) === String(h.id) || ah.name.toLowerCase() === h.name.toLowerCase())) {
          combinedAcHeads.push({
            value: String(h.id),
            id: h.id,
            name: h.name,
            label: `${h.name}${h.group ? ` (${h.group})` : ''}`,
            group: h.group || 'General',
          });
        }
      }
      setAccountHeadOptions(combinedAcHeads);

      // 2. Employees from AcHeads (Account Group "Employees" or "Partner-Employees")
      let emps = [];
      if (bData?.employees?.length > 0) {
        emps = bData.employees.map((e) => ({
          value: String(e.id || e.value),
          id: e.id || e.value,
          name: e.name,
          code: e.code || `EMP-${e.id}`,
          label: e.label || `${e.name} (${e.code || `EMP-${e.id}`})`,
          group: e.group || 'Employees',
        }));
      }
      const empAcHeads = acList.filter(
        (h) => h.group && (h.group.toLowerCase().includes('emp') || h.group.toLowerCase() === 'employees')
      );
      for (const h of empAcHeads) {
        if (!emps.some((e) => String(e.id) === String(h.id) || e.name.toLowerCase() === h.name.toLowerCase())) {
          emps.push({
            value: String(h.id),
            id: h.id,
            name: h.name,
            code: h.alias || `EMP-${h.id}`,
            label: `${h.name} (${h.alias || 'EMP-' + h.id})`,
            group: h.group || 'Employees',
          });
        }
      }
      setEmployeeOptions(emps);

      // 3. Pay Heads (with 'None' option)
      if (bData?.pay_heads?.length > 0) {
        setPayHeadOptions(
          bData.pay_heads.map((p) => ({
            value: p.value || p.name,
            id: p.id,
            label: p.label || p.name,
          }))
        );
      } else {
        setPayHeadOptions(PAY_HEAD_OPTIONS);
      }

      // 4. Loan Types / Reasons
      if (bData?.loan_reasons?.length > 0) {
        setLoanTypeOptions(
          bData.loan_reasons.map((r) => ({
            value: r.name,
            id: r.id,
            label: r.name,
          }))
        );
      }
    } catch (err) {
      console.warn('Failed to load dynamic options', err);
    }
  }, []);

  useEffect(() => {
    loadDynamicOptions();
  }, [loadDynamicOptions]);

  // Load record in edit mode
  useEffect(() => {
    if (!idQuery) return;

    async function load() {
      setIsLoading(true);
      try {
        const item = await loansApi.getLoanById(idQuery);
        if (item) {
          setFormData(item);
        } else {
          appToast.error('Loan application record not found.');
        }
      } catch (err) {
        console.error('Failed to load loan record', err);
        appToast.error('Failed to load loan details');
      } finally {
        setIsLoading(false);
      }
    }

    load();
  }, [idQuery]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleEmployeeChange = (val) => {
    if (!val) {
      setFormData((prev) => ({ ...prev, name: '', emp_id: null, employeeId: '' }));
      return;
    }
    const matched = employeeOptions.find(
      (e) => String(e.value) === String(val) || String(e.id) === String(val) || e.name === val
    );
    if (matched) {
      setFormData((prev) => ({
        ...prev,
        emp_id: matched.id || Number(matched.value) || 1,
        name: matched.name,
        employeeId: matched.code || `EMP-${matched.id}`,
      }));
    } else {
      setFormData((prev) => ({ ...prev, name: val }));
    }
  };

  const handleGuarantor1Change = (val) => {
    if (!val) {
      setFormData((prev) => ({ ...prev, guarantor1_id: null, guarantorName1: '' }));
      return;
    }
    const matched = accountHeadOptions.find(
      (h) => String(h.value) === String(val) || String(h.id) === String(val) || h.name === val
    );
    if (matched) {
      setFormData((prev) => ({
        ...prev,
        guarantor1_id: matched.id ? Number(matched.id) : null,
        guarantorName1: matched.name,
      }));
    } else {
      setFormData((prev) => ({ ...prev, guarantorName1: val }));
    }
  };

  const handleGuarantor2Change = (val) => {
    if (!val) {
      setFormData((prev) => ({ ...prev, guarantor2_id: null, guarantorName2: '' }));
      return;
    }
    const matched = accountHeadOptions.find(
      (h) => String(h.value) === String(val) || String(h.id) === String(val) || h.name === val
    );
    if (matched) {
      setFormData((prev) => ({
        ...prev,
        guarantor2_id: matched.id ? Number(matched.id) : null,
        guarantorName2: matched.name,
      }));
    } else {
      setFormData((prev) => ({ ...prev, guarantorName2: val }));
    }
  };

  const handlePayHeadChange = (val) => {
    if (!val || val === 'None' || val === '0') {
      setFormData((prev) => ({
        ...prev,
        payHead: 'None',
        pay_head_id: null,
      }));
      return;
    }
    const matched = payHeadOptions.find((p) => p.value === val || p.label === val || String(p.id) === String(val));
    setFormData((prev) => ({
      ...prev,
      payHead: matched ? matched.value : val,
      pay_head_id: matched?.id || prev.pay_head_id,
    }));
  };

  const handleLoanTypeChange = (val) => {
    const matched = loanTypeOptions.find((r) => r.value === val || r.label === val || String(r.id) === String(val));
    setFormData((prev) => ({
      ...prev,
      loanType: matched ? matched.value : val,
      loan_type: matched?.id || prev.loan_type,
      reason: matched ? matched.value : val,
    }));
  };

  const saveRecordWithStatus = async (targetStatus) => {
    if (!formData.name && !formData.emp_id) {
      appToast.error('Employee is required.');
      return;
    }
    if (Number(formData.loanAmount) <= 0) {
      appToast.error('Please enter a valid loan amount.');
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        ...formData,
        emp_id: formData.emp_id || Number(formData.employeeId?.replace(/\D/g, '')) || 1,
        loanAmount: Number(formData.loanAmount),
        emiAmount: Number(formData.emiAmount),
        status: targetStatus,
      };

      if (idQuery) {
        await loansApi.updateLoan(idQuery, payload);
        appToast.success(`Loan application updated (${targetStatus}) successfully!`);
      } else {
        await loansApi.createLoan(payload);
        appToast.success(`Loan application submitted (${targetStatus}) successfully!`);
      }

      navigate('/payroll/loans');
    } catch (err) {
      console.error('Error saving loan application', err);
      appToast.error('Failed to save loan application.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAndExit = () => saveRecordWithStatus('Pending');
  const handleApproveAndExit = () => saveRecordWithStatus('Approved');
  const handleRejectAndExit = () => saveRecordWithStatus('Rejected');
  const handleCancel = () => navigate('/payroll/loans');

  return {
    state: {
      formData,
      isEditMode: Boolean(idQuery),
      isLoading,
      isSaving,
      payHeadOptions,
      loanTypeOptions,
      employeeOptions,
      accountHeadOptions,
    },
    actions: {
      handleChange,
      handleEmployeeChange,
      handleGuarantor1Change,
      handleGuarantor2Change,
      handlePayHeadChange,
      handleLoanTypeChange,
      handleSaveAndExit,
      handleApproveAndExit,
      handleRejectAndExit,
      handleCancel,
    },
  };
}
